<template>
  <div class="dynamic-tables">
    <Breadcrumbs v-bind:breadcrumbs="breadcrumbs" />

    <Widget>
      <DataTable
        class="p-datatable-sm"
        :value="inverters"
        :paginator="true"
        :filters="filters"
        :loading="loading"
        :scrollable="true"
        scrollHeight="650px"
        :rows="1000"
        dataKey="id"
        :rowHover="true"
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        :rowsPerPageOptions="[25, 50, 100, 1000]"
        currentPageReportTemplate="Zeige {first} bis {last} von {totalRecords} Einträgen"
      >
        <template #header>
          <div class="table-header">
            <div class="table-header-left">
              <h1 class="page-title">{{ $t('inverters') }}</h1>
            </div>
            <div class="table-header-right">
              <span class="p-input-icon-left">
                <i class="pi pi-search" />
                <InputText type="text" v-model="filters['global']" :placeholder="$t('search')" />
              </span>
              <div class="button-right">
                <router-link
                  class="btn btn-success mb-2 right"
                  :to="{ name: 'InverterCreatePage' }"
                >
                  <i class="fa fa-plus-circle mr-2"></i>
                  {{ $t('inverterCreate') }}
                </router-link>
              </div>
            </div>
          </div>
        </template>
        <template #empty>
          {{ $t('no_data_found') }}
        </template>
        <template #loading>
          {{ $t('loading') }}
        </template>

        <Column field="number" header="#" sortable filterMatchMode="contains">
          <template #body="slotProps">
            <router-link
              :to="{
                name: 'InverterEditPage',
                params: { inverterNumber: slotProps.data.number },
              }"
              >{{ slotProps.data.number.toString().padStart(4, '0') }}</router-link
            >
          </template>
          <template #filter>
            <InputText type="text" v-model="filters['number']" class="p-column-filter" />
          </template>
        </Column>

        <Column
          field="manufacturer"
          :header="$t('manufacturer')"
          sortable
          filterMatchMode="contains"
        >
          <template #filter>
            <InputText type="text" v-model="filters['manufacturer']" class="p-column-filter" />
          </template>
        </Column>

        <Column field="type" :header="$t('type')" sortable filterMatchMode="contains">
          <template #filter>
            <InputText type="text" v-model="filters['type']" class="p-column-filter" />
          </template>
        </Column>

        <Column
          field="nominalPowerFormatted"
          :header="$t('nominalPower')"
          sortable
          filterMatchMode="startsWith"
        >
          <template #filter>
            <InputText
              type="text"
              v-model="filters['nominalPowerFormatted']"
              class="p-column-filter"
            />
          </template>
        </Column>

        <Column
          field="apparentPowerFormatted"
          :header="$t('apparentPower')"
          sortable
          filterMatchMode="startsWith"
        >
          <template #filter>
            <InputText
              type="text"
              v-model="filters['apparentPowerFormatted']"
              class="p-column-filter"
            />
          </template>
        </Column>

        <Column
          field="activePowerFormatted"
          :header="$t('activePower')"
          sortable
          filterMatchMode="startsWith"
        >
          <template #filter>
            <InputText
              type="text"
              v-model="filters['activePowerFormatted']"
              class="p-column-filter"
            />
          </template>
        </Column>

        <Column
          field="ratedCurrentFormatted"
          :header="$t('ratedCurrent')"
          sortable
          filterMatchMode="startsWith"
        >
          <template #filter>
            <InputText
              type="text"
              v-model="filters['ratedCurrentFormatted']"
              class="p-column-filter"
            />
          </template>
        </Column>
        <Column
          field="shortCircuitCurrentFormatted"
          :header="$t('shortCircuitCurrent')"
          sortable
          filterMatchMode="startsWith"
        >
          <template #filter>
            <InputText
              type="text"
              v-model="filters['shortCircuitCurrentFormatted']"
              class="p-column-filter"
            />
          </template>
        </Column>
        <Column
          field="nominalDcPowerFormatted"
          :header="$t('nominalDcPower')"
          sortable
          filterMatchMode="startsWith"
        >
          <template #filter>
            <InputText
              type="text"
              v-model="filters['nominalDcPowerFormatted']"
              class="p-column-filter"
            />
          </template>
        </Column>
        <Column
          field="allowedChargeDischargeCurrentFormatted"
          :header="$t('allowedChargeDischargeCurrent')"
          sortable
          filterMatchMode="startsWith"
        >
          <template #filter>
            <InputText
              type="text"
              v-model="filters['allowedChargeDischargeCurrentFormatted']"
              class="p-column-filter"
            />
          </template>
        </Column>
        <Column
          field="nominalVoltageFormatted"
          :header="$t('nominalVoltage')"
          sortable
          filterMatchMode="startsWith"
        >
          <template #filter>
            <InputText
              type="text"
              v-model="filters['nominalVoltageFormatted']"
              class="p-column-filter"
            />
          </template>
        </Column>
        <Column
          field="gridAndPlantProtectionIntegratedFormatted"
          :header="$t('gridAndPlantProtectionIntegrated')"
          sortable
          filterMatchMode="startsWith"
        >
          <template #filter>
            <InputText
              type="text"
              v-model="filters['gridAndPlantProtectionIntegratedFormatted']"
              class="p-column-filter"
            />
          </template>
        </Column>

        <Column
          field="gridAndPlantProtectionType"
          :header="$t('gridAndPlantProtectionType')"
          sortable
          filterMatchMode="startsWith"
        >
          <template #body="slotProps">
            {{ $t(slotProps.data.gridAndPlantProtectionType) }}
          </template>
          <template #filter>
            <Dropdown
              v-model="filters['gridAndPlantProtectionType']"
              :options="getGridAndPlantProtectionTypes"
              :placeholder="$t('apply_filter')"
              class="p-column-filter"
              :showClear="true"
            >
              <template #option="slotProps">
                {{ $t(slotProps.option) }}
              </template>
            </Dropdown>
          </template>
        </Column>
      </DataTable>
    </Widget>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Widget from '@/components/Widget/Widget';
import Breadcrumbs from '@/components/Breadcrumbs';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import Dropdown from 'primevue/dropdown';

export default {
  name: 'InvertersPage',
  components: {
    Breadcrumbs,
    DataTable,
    Widget,
    InputText,
    Column,
    Dropdown,
  },
  data() {
    return {
      loading: true,
      filters: {},
    };
  },
  computed: {
    ...mapGetters(['isLoading', 'getInverters', 'getGridAndPlantProtectionTypes']),
    breadcrumbs() {
      return [{ name: 'Home', route: { name: 'home' } }, { name: this.$t('inverters') }];
    },
    inverters() {
      const inverters = [];
      this.getInverters.map((inverter) => {
        inverter.nominalVoltageFormatted = this.$n(inverter.nominalVoltage, 'decimal');
        inverter.nominalPowerFormatted = this.$n(inverter.nominalPower, 'decimal_triple');
        inverter.activePowerFormatted = this.$n(inverter.activePower, 'decimal_triple');
        inverter.apparentPowerFormatted = this.$n(inverter.apparentPower, 'decimal_triple');
        inverter.ratedCurrentFormatted = this.$n(inverter.ratedCurrent, 'decimal_triple');
        inverter.nominalDcPowerFormatted = this.$n(inverter.nominalDcPower, 'decimal_triple');
        inverter.gridAndPlantProtectionIntegratedFormatted = this.$n(
          inverter.gridAndPlantProtectionIntegrated,
          'decimal_triple'
        );
        inverter.shortCircuitCurrentFormatted = this.$n(
          inverter.shortCircuitCurrent,
          'decimal_triple'
        );
        inverter.allowedChargeDischargeCurrentFormatted = this.$n(
          inverter.allowedChargeDischargeCurrent,
          'decimal_triple'
        );
        inverters.push(inverter);
      });

      return inverters;
    },
  },
  methods: {
    ...mapActions(['fetchInverters', 'fetchGridAndPlantProtectionTypes']),
  },
  async created() {
    await this.fetchInverters();
    await this.fetchGridAndPlantProtectionTypes();

    this.loading = false;
  },
};
</script>

<style scoped lang="scss">
::v-deep .p-datatable .p-datatable-thead > tr > th:nth-child(1),
::v-deep .p-datatable .p-datatable-tbody > tr > td:nth-child(1) {
  width: 60px;
  text-align: center;
}
::v-deep .p-datatable .p-datatable-thead > tr > th:nth-child(n + 4),
::v-deep .p-datatable .p-datatable-tbody > tr > td:nth-child(n + 4) {
  width: 130px;
  text-align: center;
}
.table-header {
  display: flex;
  justify-content: space-between;
}
.table-header-left {
  display: flex;
  justify-content: flex-start;
}
.table-header-right {
  display: flex;
  justify-content: flex-end;
  column-gap: 1em;
}
/** Fix Dropdown overlay */
::v-deep .p-datatable-scrollable-header {
  overflow: visible;
}
</style>
